import { EMPTY, inBounds, indexOf } from './board.js';
import { applyGravity } from './gravity-resolver.js';
import { nextInt } from './rng.js';
import { runLength } from './virus-placement.js';
const NEIGHBORS = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
];
export function bossBodyCells(config) {
    const body = config.body;
    if (!body)
        return [];
    const cells = [];
    for (let y = body.y; y < body.y + body.height; y++) {
        for (let x = body.x; x < body.x + body.width; x++)
            cells.push({ x, y });
    }
    return cells;
}
/** Ставит тело босса и части гибрида на поле. */
export function placeBoss(board, config) {
    const cells = board.cells.slice();
    for (const p of bossBodyCells(config))
        cells[indexOf(board, p.x, p.y)] = { kind: 'boss' };
    config.parts.forEach((p, part) => {
        cells[indexOf(board, p.x, p.y)] = { kind: 'boss', part };
    });
    return { ...board, cells };
}
/** Выбирает до count разных элементов (частичное перемешивание). */
function pickDistinct(items, count, rng) {
    const pool = items.slice();
    const picked = [];
    let state = rng;
    for (let k = 0; k < count && k < pool.length; k++) {
        const r = nextInt(state, pool.length - k);
        state = r.state;
        const j = k + r.value;
        const chosen = pool[j];
        pool[j] = pool[k];
        pool[k] = chosen;
        picked.push(chosen);
    }
    return { picked, rng: state };
}
function pointsWhere(board, test) {
    const points = [];
    board.cells.forEach((cell, i) => {
        const x = i % board.width;
        const y = Math.floor(i / board.width);
        if (test(cell, x, y))
            points.push({ x, y });
    });
    return points;
}
/** Делает count случайных вирусов сторожевыми (в «Биоплёнке»). */
export function placeGuards(board, count, rng) {
    const viruses = pointsWhere(board, (c) => c.kind === 'virus');
    const { picked, rng: state } = pickDistinct(viruses, count, rng);
    const cells = board.cells.slice();
    for (const p of picked) {
        const cell = cells[indexOf(board, p.x, p.y)];
        if (cell?.kind === 'virus') {
            cells[indexOf(board, p.x, p.y)] = {
                kind: 'virus',
                color: cell.color,
                shield: true,
                guard: true,
            };
        }
    }
    return { board: { ...board, cells }, rng: state };
}
/** Позиции частей гибрида, которых сейчас нет на поле. */
function missingParts(board, config) {
    return config.parts.flatMap((p, part) => {
        const cell = board.cells[indexOf(board, p.x, p.y)];
        return cell?.kind === 'boss' && cell.part === part ? [] : [part];
    });
}
/** Отмечает клетки будущего эффекта способности. */
function planTargets(spec, board, rules, config, rng) {
    switch (spec.kind) {
        case 'replicate': {
            const empty = pointsWhere(board, (c, _x, y) => c.kind === 'empty' && y >= rules.virusTopRow);
            const { picked, rng: afterPick } = pickDistinct(empty, spec.count, rng);
            let state = afterPick;
            const targets = picked.map((p) => {
                const color = nextInt(state, rules.colorCount);
                state = color.state;
                return { ...p, color: color.value };
            });
            return { targets, rng: state };
        }
        case 'mutate': {
            const viruses = pointsWhere(board, (c) => c.kind === 'virus');
            const { picked, rng: afterPick } = pickDistinct(viruses, spec.count, rng);
            let state = afterPick;
            const targets = picked.map((p) => {
                const cell = board.cells[indexOf(board, p.x, p.y)];
                const old = cell?.kind === 'virus' ? cell.color : 0;
                if (rules.colorCount < 2)
                    return { ...p, color: old };
                const shift = nextInt(state, rules.colorCount - 1);
                state = shift.state;
                return { ...p, color: (old + 1 + shift.value) % rules.colorCount };
            });
            return { targets, rng: state };
        }
        case 'fog': {
            const x = nextInt(rng, rules.width - spec.count + 1);
            return { targets: [{ x: x.value, y: 0 }], rng: x.state };
        }
        case 'regrow': {
            const part = missingParts(board, config)[0];
            const p = part === undefined ? undefined : config.parts[part];
            return { targets: p ? [p] : [], rng };
        }
        case 'raise-floor':
        case 'storm':
        case 'latency':
            return { targets: [], rng };
    }
}
function abilityEnabled(spec, hp) {
    return spec.belowHp === null || hp <= spec.belowHp;
}
/** Начальное состояние босса: способности без условия по здоровью сразу начинают отсчёт. */
export function createBossState(rules, board, rng) {
    const config = rules.boss;
    if (!config)
        throw new RangeError('rules.boss is not set');
    let state = rng;
    const abilities = config.abilities.map((spec) => {
        if (spec.every === null || !abilityEnabled(spec, config.hp)) {
            return { countdown: null, done: false, targets: [] };
        }
        const planned = planTargets(spec, board, rules, config, state);
        state = planned.rng;
        return { countdown: spec.every, done: false, targets: planned.targets };
    });
    return { hp: config.hp, rng: state, abilities, fog: null, storm: 0, dormant: 0 };
}
export function isBossInvulnerable(board, boss, config) {
    return bossBlockReason(board, boss, config) !== null;
}
function bossBlockReason(board, boss, config) {
    if (boss.dormant > 0)
        return 'latency';
    if (config.guards > 0 && board.cells.some((c) => c.kind === 'virus' && c.guard)) {
        return 'biofilm';
    }
    return null;
}
/**
 * Урон боссу от одного шага цепочки. board — поле после очистки шага (до гравитации).
 * vessel: шаг, в котором очищена клетка рядом с телом (по горизонтали или вертикали);
 * outside: любой шаг; hybrid: каждая часть рядом с очищенной клеткой уничтожается.
 */
export function hitBoss(board, cleared, chain, boss, config, events) {
    if (boss.hp <= 0 || cleared.length === 0)
        return { board, boss };
    const neighbors = (test) => {
        const found = new Map();
        for (const c of cleared) {
            for (const [dx, dy] of NEIGHBORS) {
                const x = c.x + dx;
                const y = c.y + dy;
                if (!inBounds(board, x, y))
                    continue;
                const i = indexOf(board, x, y);
                const cell = board.cells[i];
                if (cell && test(cell))
                    found.set(i, { x, y });
            }
        }
        return [...found.values()];
    };
    const stepDamage = config.damage + config.chainBonus * (chain - 1);
    let amount = 0;
    let parts = [];
    if (config.placement === 'outside') {
        amount = stepDamage;
    }
    else if (config.placement === 'vessel') {
        if (neighbors((c) => c.kind === 'boss' && c.part === undefined).length > 0)
            amount = stepDamage;
    }
    else {
        parts = neighbors((c) => c.kind === 'boss' && c.part !== undefined);
        amount = parts.length * config.partDamage;
    }
    if (amount === 0)
        return { board, boss };
    const blocked = bossBlockReason(board, boss, config);
    if (blocked) {
        events.push({ type: 'boss-blocked', reason: blocked });
        return { board, boss };
    }
    let next = board;
    if (parts.length > 0) {
        const cells = board.cells.slice();
        for (const p of parts)
            cells[indexOf(board, p.x, p.y)] = EMPTY;
        next = { ...board, cells };
    }
    const hp = Math.max(0, boss.hp - amount);
    events.push({ type: 'boss-damaged', amount: boss.hp - hp, hp, parts });
    if (hp === 0)
        events.push({ type: 'boss-defeated' });
    return { board: next, boss: { ...boss, hp } };
}
/** Обрывает связи половин капсул, у которых не осталось пары (после сдвига колонок). */
function fixLinks(board) {
    const partnerOf = {
        left: [-1, 0, 'right'],
        right: [1, 0, 'left'],
        up: [0, -1, 'down'],
        down: [0, 1, 'up'],
    };
    const cells = board.cells.map((cell, i) => {
        if (cell.kind !== 'segment' || cell.link === 'none')
            return cell;
        const [dx, dy, back] = partnerOf[cell.link] ?? [0, 0, ''];
        const x = (i % board.width) + dx;
        const y = Math.floor(i / board.width) + dy;
        const partner = inBounds(board, x, y) ? board.cells[indexOf(board, x, y)] : undefined;
        return partner?.kind === 'segment' && partner.link === back
            ? cell
            : { kind: 'segment', color: cell.color, link: 'none' };
    });
    return { ...board, cells };
}
/**
 * «Подъём дна». В каждой колонке содержимое нижнего участка (от дна до первой стенки или тела
 * босса) сдвигается на клетку вверх; в нижнюю клетку встаёт вирус (в count случайных колонках)
 * или пустота. Если выталкивается занятая клетка верхней строки — переполнение;
 * если участок упирается в стенку или босса — верхняя клетка участка раздавливается.
 */
function raiseFloor(board, rules, count, rng) {
    const cells = board.cells.slice();
    const bottom = board.height - 1;
    const fixed = (x, y) => {
        const kind = cells[indexOf(board, x, y)]?.kind;
        return kind === 'wall' || kind === 'boss';
    };
    const columns = [];
    for (let x = 0; x < board.width; x++)
        if (!fixed(x, bottom))
            columns.push(x);
    const { picked, rng: afterPick } = pickDistinct(columns, count, rng);
    let state = afterPick;
    let overflow = false;
    for (const x of columns) {
        let top = bottom;
        while (top > 0 && !fixed(x, top - 1))
            top--;
        if (top === 0 && cells[indexOf(board, x, 0)]?.kind !== 'empty')
            overflow = true;
        for (let y = top; y < bottom; y++) {
            cells[indexOf(board, x, y)] = cells[indexOf(board, x, y + 1)] ?? EMPTY;
        }
        cells[indexOf(board, x, bottom)] = EMPTY;
    }
    const placed = [];
    for (const x of picked.slice().sort((a, b) => a - b)) {
        const start = nextInt(state, rules.colorCount);
        state = start.state;
        for (let k = 0; k < rules.colorCount; k++) {
            const color = (start.value + k) % rules.colorCount;
            if (runLength(cells, board, x, bottom, color, 1, 0) < rules.matchLength &&
                runLength(cells, board, x, bottom, color, 0, 1) < rules.matchLength) {
                cells[indexOf(board, x, bottom)] = { kind: 'virus', color };
                placed.push({ x, y: bottom });
                break;
            }
        }
    }
    const settled = applyGravity(fixLinks({ ...board, cells })).board;
    return { board: settled, rng: state, overflow, cells: placed };
}
/**
 * Ход босса после каждой зафиксированной капсулы (если босс жив):
 * 1) отсчёт эффектов (туман, шторм, сон; по пробуждении — «Подъём дна»);
 * 2) способности: включение по порогу здоровья, отсчёт, срабатывание, новые отметки.
 */
export function bossTurn(board, boss, rules, events) {
    const config = rules.boss;
    if (!config || boss.hp <= 0)
        return { board, boss, overflow: false };
    let current = board;
    let rng = boss.rng;
    let overflow = false;
    let fog = boss.fog && boss.fog.capsulesLeft > 1
        ? { ...boss.fog, capsulesLeft: boss.fog.capsulesLeft - 1 }
        : null;
    let storm = Math.max(0, boss.storm - 1);
    let dormant = boss.dormant;
    let hp = boss.hp;
    const raise = (count, kind) => {
        const raised = raiseFloor(current, rules, count, rng);
        current = raised.board;
        rng = raised.rng;
        overflow ||= raised.overflow;
        events.push({ type: 'boss-ability', ability: kind, cells: raised.cells });
    };
    if (dormant > 0) {
        dormant--;
        if (dormant === 0) {
            const latency = config.abilities.find((a) => a.kind === 'latency');
            raise(latency?.count ?? 0, 'raise-floor');
        }
    }
    const fire = (spec, targets) => {
        const cells = current.cells.slice();
        const at = (p) => cells[indexOf(current, p.x, p.y)];
        const affected = [];
        switch (spec.kind) {
            case 'replicate':
                for (const t of targets) {
                    if (at(t)?.kind !== 'empty')
                        continue;
                    for (let k = 0; k < rules.colorCount; k++) {
                        const color = ((t.color ?? 0) + k) % rules.colorCount;
                        if (runLength(cells, current, t.x, t.y, color, 1, 0) < rules.matchLength &&
                            runLength(cells, current, t.x, t.y, color, 0, 1) < rules.matchLength) {
                            cells[indexOf(current, t.x, t.y)] = { kind: 'virus', color };
                            affected.push({ x: t.x, y: t.y });
                            break;
                        }
                    }
                }
                break;
            case 'mutate':
                for (const t of targets) {
                    const cell = at(t);
                    if (cell?.kind !== 'virus' || t.color === undefined)
                        continue;
                    cells[indexOf(current, t.x, t.y)] = { ...cell, color: t.color };
                    affected.push({ x: t.x, y: t.y });
                }
                break;
            case 'regrow':
                for (const part of missingParts(current, config)) {
                    const p = config.parts[part];
                    if (!p || at(p)?.kind !== 'empty')
                        continue;
                    cells[indexOf(current, p.x, p.y)] = { kind: 'boss', part };
                    hp = Math.min(config.hp, hp + config.partDamage);
                    affected.push(p);
                    break;
                }
                break;
            case 'fog': {
                const x = targets[0]?.x ?? 0;
                fog = { x, width: spec.count, capsulesLeft: spec.duration };
                for (let dx = 0; dx < spec.count; dx++)
                    affected.push({ x: x + dx, y: 0 });
                break;
            }
            case 'storm':
                storm = spec.duration;
                break;
            case 'latency':
                dormant = spec.duration;
                break;
            case 'raise-floor':
                raise(spec.count, 'raise-floor');
                return;
        }
        current = { ...current, cells };
        events.push({ type: 'boss-ability', ability: spec.kind, cells: affected });
    };
    const abilities = config.abilities.map((spec, i) => {
        const prev = boss.abilities[i] ?? { countdown: null, done: false, targets: [] };
        if (prev.done)
            return prev;
        if (!abilityEnabled(spec, hp))
            return { countdown: null, done: false, targets: [] };
        if (spec.every === null) {
            fire(spec, prev.targets);
            return { countdown: null, done: true, targets: [] };
        }
        const plan = () => {
            const planned = planTargets(spec, current, rules, config, rng);
            rng = planned.rng;
            return { countdown: spec.every, done: false, targets: planned.targets };
        };
        if (prev.countdown === null)
            return plan();
        const countdown = prev.countdown - 1;
        if (countdown > 0) {
            // Отметки «Отращивания» следят за тем, какая часть сейчас уничтожена.
            if (spec.kind === 'regrow') {
                return {
                    ...prev,
                    countdown,
                    targets: planTargets(spec, current, rules, config, rng).targets,
                };
            }
            return { ...prev, countdown };
        }
        fire(spec, prev.targets);
        return plan();
    });
    return {
        board: current,
        boss: { hp, rng, abilities, fog, storm, dormant },
        overflow,
    };
}
//# sourceMappingURL=boss.js.map